import { CommonModule } from "@angular/common";
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MenuItem } from "primeng/api";
import { TranslatePipe } from "../../pipe/translation.filter.pipe";
import { AdminPageContainerComponent } from "src/app/components/ape-admin-page-container/ape-admin-page-container.component";
import { MonitoringDetailsCountersTemplateComponent } from "src/app/templates/monitoring-details-counters-template/monitoring-details-counters-template.component";
import { StatusCardData } from "src/app/components/ape-status-card/ape-status-card.component";
import { TRANSLATION_KEYS } from "src/app/constants/translations/translation-keys";
import { BackofficeProcessPhases, MonitoringUserFiltersValues, PhaseStatusEnumTypes, ProcessStatusEnum, ProcessStatusEnumTypes } from "alloy-performance-engine-cl";
import { ApeAdminMonitoringUsersTableComponent } from "src/app/components/ape-admin-monitoring-users-table/ape-admin-monitoring-users-table.component";
import { ApeTablePaginationComponent } from "src/app/components/ape-table-pagination/ape-table-pagination.component";
import { ApeButtonComponent } from "src/app/components/ape-button/ape-button.component";
import { ApeMonitoringDetailsFilterProcessDrawerComponent } from "src/app/components/ape-monitoring-details-filter-process-drawer/ape-monitoring-details-filter-process-drawer.component";
import { ApePagePlaceholderComponent } from "src/app/components/ape-page-placeholder/ape-page-placeholder.component";
import { ApeInputComponent } from "src/app/components/ape-input/ape-input.component";
import { AngularSvgIconModule } from "angular-svg-icon";
import { MonitoringBackofficeApiService, MonitoringUserSummaryExtended, MonitoringUserSummaryIdsExtended } from "src/app/services/api/monitoring-backoffice-api.service";
import { Router } from "@angular/router";
import { ApeStatusBadgeComponent } from "src/app/components/ape-status-badge/ape-status-badge.component";
import { ApeSelectComponent } from "src/app/components/ape-select/ape-select.component";
import { LayoutService } from "src/app/services/layout.service";
import { ApeProcessSetupSkeletonComponent } from "src/app/components/ape-process-setup-skeleton/ape-process-setup-skeleton.component";
import { ApeCalibrationTalentMatrixConfirmationModalComponent, PersonSummary } from "src/app/components/ape-calibration-talent-matrix-confirmation-modal/ape-calibration-talent-matrix-confirmation-modal.component";
import { ApeModalComponent } from "src/app/components/ape-modal/ape-modal.component";
import { LoggerService } from "src/ancestors/logger.service";
import { AlertService } from "src/app/services/alert.service";
import { TranslateService } from "src/app/util/translate.service";
import { ApeDownloadButtonComponent } from "src/app/components/ape-download-button/ape-download-button.component";

@Component({
  selector: "process-monitoring-details-template",
  standalone: true,
  host: { class: "w-full h-full" },
  templateUrl: "./process-monitoring-details-template.component.html",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslatePipe,
    AdminPageContainerComponent,
    MonitoringDetailsCountersTemplateComponent,
    ApeAdminMonitoringUsersTableComponent,
    ApeTablePaginationComponent,
    ApeButtonComponent,
    ApeMonitoringDetailsFilterProcessDrawerComponent,
    ApePagePlaceholderComponent,
    ApeInputComponent,
    AngularSvgIconModule,
    ApeStatusBadgeComponent,
    ApeSelectComponent,
    ApeProcessSetupSkeletonComponent,
    ApeModalComponent,
    ApeProcessSetupSkeletonComponent,
    ApeCalibrationTalentMatrixConfirmationModalComponent,
    ApeDownloadButtonComponent
  ]
})
export class ApeMonitoringDetailsTemplateComponent implements OnInit, OnDestroy {
  /**
   * Chiavi di traduzione per type safety
   */
  readonly TRANSLATION_KEYS = TRANSLATION_KEYS;

  private router = inject(Router);

  private layoutService = inject(LayoutService);

  private logger = inject(LoggerService);

  private monitoringBackofficeApiService = inject(MonitoringBackofficeApiService);

  private alertService = inject(AlertService);

  private translate = inject(TranslateService);

  // gestione loading
  @Input() loading: boolean = false;

  @Input() monitoringDetailsCountersCardData: StatusCardData[] = [];

  @Input() processForm!: FormGroup;

  @Input() feedback360StatusOptions: MenuItem[] = [];

  @Input() selfAssessmentStatusOptions: MenuItem[] = [];

  @Input() evaluationStatusOptions: MenuItem[] = [];

  @Input() deliveryStatusOptions: MenuItem[] = [];

  @Input() companyOptions: MenuItem[] = [];

  @Input() officeOptions: MenuItem[] = [];

  @Input() departmentOptions: MenuItem[] = [];

  @Input() BUOptions: MenuItem[] = [];

  @Input() evaluatorsOptions: MenuItem[] = [];

  @Input() selectedCompanies: MenuItem[] = [];

  @Input() selectedOffices: MenuItem[] = [];

  @Input() selectedDepartments: MenuItem[] = [];

  @Input() selectedBUs: MenuItem[] = [];

  @Input() selectedEvaluators: MenuItem[] = [];

  @Input() selectedFeedback360Statuses: MenuItem[] = [];

  @Input() selectedSelfAssessmentStatuses: MenuItem[] = [];

  @Input() selectedEvaluationStatuses: MenuItem[] = [];

  @Input() selectedDeliveryStatuses: MenuItem[] = [];

  @Input() appliedCompanyOptions: MenuItem[] = [];

  @Input() appliedOfficeOptions: MenuItem[] = [];

  @Input() appliedDepartmentOptions: MenuItem[] = [];

  @Input() appliedBUOptions: MenuItem[] = [];

  @Input() appliedEvaluatorsOptions: MenuItem[] = [];

  @Input() appliedFeedback360Statuses: MenuItem[] = [];

  @Input() appliedSelfAssessmentStatuses: MenuItem[] = [];

  @Input() appliedEvaluationStatuses: MenuItem[] = [];

  @Input() appliedDeliveryStatuses: MenuItem[] = [];

  @Input() usersDataCount: number = 0;

  @Input() usersData: MonitoringUserSummaryExtended[] = [];

  @Input() processPhases: BackofficeProcessPhases | null = null;

  @Input() processStatus: ProcessStatusEnum | null = null;

  @Input() allUsersData: MonitoringUserSummaryIdsExtended[] = [];

  @Input() userFilterData: MonitoringUserFiltersValues | null = null;

  @Input() loadingUsersData: boolean = false;

  @Input() loadingUsersDataCount: boolean = false;

  @Input() loadingUserFilterData: boolean = false;

  @Input() processName: string | null = null;

  @Input() allUsersChecked: boolean = false;

  @Input() totalPages: number = 0;

  @Input() page: number = 1;

  @Input() pageSize: number = 50;

  @Input() isDrawerVisible: boolean = false;

  @Input() searchedText: string = "";

  @Input() sortOptions: MenuItem[] = [];

  @Input() selectedSortOption: MenuItem | undefined = undefined;

  @Input() processId: string | null = null;

  @Input() showConfirmationModal: boolean = false;

  @Input() confirmationSummary: PersonSummary[] = [];

  @Input() isLoadingConfirmation: boolean = false;

  // Output events
  @Output() backClick = new EventEmitter<void>();

  @Output() onCloseDrawer = new EventEmitter<void>();

  @Output() onFeedback360StatusesChange = new EventEmitter<MenuItem[]>();

  @Output() onSelfAssessmentStatusesChange = new EventEmitter<MenuItem[]>();

  @Output() onEvaluationStatusesChange = new EventEmitter<MenuItem[]>();

  @Output() onDeliveryStatusesChange = new EventEmitter<MenuItem[]>();

  @Output() onResetFilters = new EventEmitter<void>();

  @Output() onApplyFilters = new EventEmitter<void>();

  @Output() onAccessProcess = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() onImpersonateUser = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() onImpersonateManager = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() onPageChange = new EventEmitter<number>();

  @Output() onPageSizeChange = new EventEmitter<number>();

  @Output() onAdvancedFiltersClicked = new EventEmitter<void>();

  @Output() onSortChange = new EventEmitter<MenuItem>();

  @Output() onCompanyChange = new EventEmitter<MenuItem[]>();

  @Output() onEvaluatorChange = new EventEmitter<MenuItem[]>();

  @Output() onDepartmentChange = new EventEmitter<MenuItem[]>();

  @Output() onOfficeChange = new EventEmitter<MenuItem[]>();

  @Output() onBUChange = new EventEmitter<MenuItem[]>();

  @Output() onSearchChange = new EventEmitter<string>();

  @Output() onSearchUsers = new EventEmitter<void>();

  @Output() onShowConfirmDrawerClose = new EventEmitter<void>();

  @Output() onRemoveFilterFromPage = new EventEmitter<MenuItem>();

  @Output() onExportProcessReport = new EventEmitter<string>();

  @Output() onReopenEvaluation = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() onReopenSelfEvaluation = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() onSolicitEvaluation = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() onSolicitSelfEvaluation = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() onSolicitMassiveEvaluation = new EventEmitter<void>();

  @Output() onSolicitMassiveSelfEvaluation = new EventEmitter<void>();

  @Output() onDownloadEvaluationCard = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() onSendCalibrationCards = new EventEmitter<MonitoringUserSummaryExtended>();

  protected isVisibleReopenEvaluationModal: boolean = false;

  protected isVisibleReopenSelfEvaluationModal: boolean = false;

  protected isVisibleSolicitEvaluationModal: boolean = false;

  protected isVisibleSolicitSelfEvaluationModal: boolean = false;

  protected isLoadingSolicitMassiveEvaluation: boolean = false;

  protected isLoadingSolicitMassiveSelfEvaluation: boolean = false;

  protected isVisibleSolicitMassiveEvaluationModal: boolean = false;

  protected isVisibleSolicitMassiveSelfEvaluationModal: boolean = false;

  protected selectedUser: MonitoringUserSummaryExtended | null = null;

  @Output() onConfirmAndSend = new EventEmitter<void>();

  @Output() onConfirmModal = new EventEmitter<void>();

  @Output() onCancelModal = new EventEmitter<void>();

  @Output() onCloseModal = new EventEmitter<void>();

  isMdOrSmaller: boolean = false;

  constructor() { }

  ngOnInit(): void {
    this.isMdOrSmaller = window.innerWidth <= 768;
    this.layoutService.setWhiteBackground();
  }

  ngOnDestroy(): void {
    this.layoutService.resetToNormal();
  }

  protected advancedFiltersClicked(): void {
    this.onAdvancedFiltersClicked.emit();
  }

  protected onFeedback360StatusesChangeHandler(selected: MenuItem[]): void {
    this.selectedFeedback360Statuses = selected;
    this.onFeedback360StatusesChange.emit(selected);
  }

  protected onSelfAssessmentStatusesChangeHandler(selected: MenuItem[]): void {
    this.selectedSelfAssessmentStatuses = selected;
    this.onSelfAssessmentStatusesChange.emit(selected);
  }

  protected onEvaluationStatusesChangeHandler(selected: MenuItem[]): void {
    this.selectedEvaluationStatuses = selected;
    this.onEvaluationStatusesChange.emit(selected);
  }

  protected onDeliveryStatusesChangeHandler(selected: MenuItem[]): void {
    this.selectedDeliveryStatuses = selected;
    this.onDeliveryStatusesChange.emit(selected);
  }

  protected onCompanyChangeHandler(selected: MenuItem[]): void {
    this.selectedCompanies = selected;
    this.onCompanyChange.emit(selected);
  }

  protected onEvaluatorChangeHandler(selected: MenuItem[]): void {
    this.selectedEvaluators = selected;
    this.onEvaluatorChange.emit(selected);
  }

  protected onDepartmentChangeHandler(selected: MenuItem[]): void {
    this.selectedDepartments = selected;
    this.onDepartmentChange.emit(selected);
  }

  protected onOfficeChangeHandler(selected: MenuItem[]): void {
    this.selectedOffices = selected;
    this.onOfficeChange.emit(selected);
  }

  protected onBUChangeHandler(selected: MenuItem[]): void {
    this.selectedBUs = selected;
    this.onBUChange.emit(selected);
  }

  getActiveFiltersOnPage(): MenuItem[] {
    const activeFilters = [];

    // Usa i filtri applicati, non quelli selezionati
    if (this.appliedFeedback360Statuses.length > 0) {
      const statuses = this.appliedFeedback360Statuses;
      for (const status of statuses) {
        activeFilters.push({
          id: status.id,
          label: status.label,
          title: status.title
        });
      }
    }

    if (this.appliedSelfAssessmentStatuses.length > 0) {
      const statuses = this.appliedSelfAssessmentStatuses;
      for (const status of statuses) {
        activeFilters.push({
          id: status.id,
          label: status.label,
          title: status.title
        });
      }
    }

    if (this.appliedEvaluationStatuses.length > 0) {
      const statuses = this.appliedEvaluationStatuses;
      for (const status of statuses) {
        activeFilters.push({
          id: status.id,
          label: status.label,
          title: status.title
        });
      }
    }

    if (this.appliedDeliveryStatuses.length > 0) {
      const statuses = this.appliedDeliveryStatuses;
      for (const status of statuses) {
        activeFilters.push({
          id: status.id,
          label: status.label,
          title: status.title
        });
      }
    }
    if (this.appliedCompanyOptions.length > 0) {
      const companyOptions = this.appliedCompanyOptions;
      for (const company of companyOptions) {
        activeFilters.push({
          id: company.id,
          label: company.label,
          title: company.title
        });
      }
    }
    if (this.appliedEvaluatorsOptions.length > 0) {
      const evaluatorsOptions = this.appliedEvaluatorsOptions;
      for (const evaluator of evaluatorsOptions) {
        activeFilters.push({
          id: evaluator.id,
          label: evaluator.label,
          title: evaluator.title
        });
      }
    }
    if (this.appliedDepartmentOptions.length > 0) {
      const departmentOptions = this.appliedDepartmentOptions;
      for (const department of departmentOptions) {
        activeFilters.push({
          id: department.id,
          label: department.label,
          title: department.title
        });
      }
    }
    if (this.appliedOfficeOptions.length > 0) {
      const officeOptions = this.appliedOfficeOptions;
      for (const office of officeOptions) {
        activeFilters.push({
          id: office.id,
          label: office.label,
          title: office.title
        });
      }
    }
    if (this.appliedBUOptions.length > 0) {
      const BUOptions = this.appliedBUOptions;
      for (const BU of BUOptions) {
        activeFilters.push({
          id: BU.id,
          label: BU.label,
          title: BU.title
        });
      }
    }
    return activeFilters || [];
  }

  getSelectedUsersCount(): number {
    return this.allUsersData.filter(p => p.checked).length || 0;
  }

  protected allUsersCheckedChange(event: boolean): void {
    this.allUsersChecked = event;
    this.usersData.forEach(p => p.checked = event);
    this.allUsersData.forEach(p => p.checked = event);
    const ids = this.allUsersData.filter(p => p.checked)
      .map(p => p.userId);
    sessionStorage.setItem("userIdsChecked", ids.join(","));
  }

  getSelectedUsersCountCanAccessMatrix(): number {
    return this.allUsersData.filter(p => p.checked && p.canAccessCalibration).length || 0;
  }

  getSelectedUsersCountCanConfirmAndSend(): number {
    return this.allUsersData.filter(p => p.checked && p.canConfirmAndSendCalibration).length || 0;
  }

  protected resetFilters(): void {
    this.onResetFilters.emit();
  }

  protected searchChange(event: string): void {
    this.onSearchChange.emit(event);
  }

  protected searchUsers(): void {
    this.onSearchUsers.emit();
  }

  protected goToMatrix(): void {
    void this.router.navigate(["/admin/calibration", this.processId]);
  }

  protected confirmAndSend(): void {
    this.onConfirmAndSend.emit();
  }

  protected onConfirmModalHandler(): void {
    this.onConfirmModal.emit();
  }

  protected onCancelModalHandler(): void {
    this.onCancelModal.emit();
  }

  protected onCloseModalHandler(): void {
    this.onCloseModal.emit();
  }

  trackByFn(index: number): number {
    return index;
  }

  /**
   * Rimuove un filtro dalla pagina
   * Il filtro viene rimosso sia dagli applied che dai selected
   * @param filter - Filtro da rimuovere
   */
  protected removeFilterFromPage(filter: MenuItem): void {
    // Rimuovi dai filtri applicati (controlla sia id che label per evitare conflitti tra categorie diverse)
    this.appliedFeedback360Statuses = this.appliedFeedback360Statuses.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.appliedSelfAssessmentStatuses = this.appliedSelfAssessmentStatuses.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.appliedEvaluationStatuses = this.appliedEvaluationStatuses.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.appliedDeliveryStatuses = this.appliedDeliveryStatuses.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.appliedCompanyOptions = this.appliedCompanyOptions.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.appliedEvaluatorsOptions = this.appliedEvaluatorsOptions.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.appliedDepartmentOptions = this.appliedDepartmentOptions.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.appliedOfficeOptions = this.appliedOfficeOptions.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.appliedBUOptions = this.appliedBUOptions.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));

    // Rimuovi dai filtri selezionati (controlla sia id che label)
    this.selectedFeedback360Statuses = this.selectedFeedback360Statuses.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.selectedSelfAssessmentStatuses = this.selectedSelfAssessmentStatuses.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.selectedEvaluationStatuses = this.selectedEvaluationStatuses.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.selectedDeliveryStatuses = this.selectedDeliveryStatuses.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.selectedCompanies = this.selectedCompanies.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.selectedEvaluators = this.selectedEvaluators.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.selectedDepartments = this.selectedDepartments.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.selectedOffices = this.selectedOffices.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));
    this.selectedBUs = this.selectedBUs.filter(f => !(f.id === filter.id && f.label === filter.label && f.title === filter.title));

    // Emetti evento per la pagina (reset paginazione + recupero dati)
    this.onRemoveFilterFromPage.emit(filter);
  }

  protected showReopenEvaluationModal(user: MonitoringUserSummaryExtended) {
    this.selectedUser = user;
    this.isVisibleReopenEvaluationModal = true;
  }

  protected showReopenSelfEvaluationModal(user: MonitoringUserSummaryExtended) {
    this.selectedUser = user;
    this.isVisibleReopenSelfEvaluationModal = true;
  }

  protected showSolicitEvaluationModal(user: MonitoringUserSummaryExtended) {
    this.selectedUser = user;
    this.isVisibleSolicitEvaluationModal = true;
  }

  protected showSolicitSelfEvaluationModal(user: MonitoringUserSummaryExtended) {
    this.selectedUser = user;
    this.isVisibleSolicitSelfEvaluationModal = true;
  }

  protected onCancelReopenEvaluation() {
    this.isVisibleReopenEvaluationModal = false;
  }

  protected onCancelReopenSelfEvaluation() {
    this.isVisibleReopenSelfEvaluationModal = false;
  }

  protected onCancelSolicitEvaluation() {
    this.isVisibleSolicitEvaluationModal = false;
  }

  protected onCancelSolicitSelfEvaluation() {
    this.isVisibleSolicitSelfEvaluationModal = false;
  }

  protected onConfirmReopenEvaluation() {
    this.isVisibleReopenEvaluationModal = false;
    if (this.selectedUser) {
      this.onReopenEvaluation.emit(this.selectedUser);
    }
  }

  protected onConfirmReopenSelfEvaluation() {
    this.isVisibleReopenSelfEvaluationModal = false;
    if (this.selectedUser) {
      this.onReopenSelfEvaluation.emit(this.selectedUser);
    }
  }

  protected onConfirmSolicitEvaluation() {
    this.isVisibleSolicitEvaluationModal = false;
    if (this.selectedUser) {
      this.onSolicitEvaluation.emit(this.selectedUser);
    }
  }

  protected onConfirmSolicitSelfEvaluation() {
    this.isVisibleSolicitSelfEvaluationModal = false;
    if (this.selectedUser) {
      this.onSolicitSelfEvaluation.emit(this.selectedUser);
    }
  }

  protected async onConfirmSolicitMassiveEvaluation() {
    if (!this.processId) {
      this.logger.error(`Cannot solicit massive evaluation missing processId (${this.processId})`);
      this.isVisibleSolicitMassiveEvaluationModal = false;
      return;
    } else {
      try {
        this.isLoadingSolicitMassiveEvaluation = true;
        this.isVisibleSolicitMassiveEvaluationModal = false;
        await this.monitoringBackofficeApiService.solicitAllUsersEvaluation(this.processId);
        this.onSolicitMassiveEvaluation.emit();
      } catch (error) {
        this.logger.error(`Error soliciting massive evaluation: ${error?.toString()}`);
        this.alertService.showError(this.translate.instant("admin.monitoring.details.SOLICIT_MASSIVE_EVALUATION_ERROR"));
      } finally {
        this.isVisibleSolicitMassiveEvaluationModal = false;
        this.isLoadingSolicitMassiveEvaluation = false;
      }
    }
  }

  protected onCancelSolicitMassiveEvaluation() {
    // previene di fare annulla e chiudere la modale se stiamo caricando
    if (this.isLoadingSolicitMassiveEvaluation) {
      return;
    }
    this.isVisibleSolicitMassiveEvaluationModal = false;
  }

  protected async onConfirmSolicitMassiveSelfEvaluation() {
    if (!this.processId) {
      this.logger.error(`Cannot solicit massive self evaluation missing processId (${this.processId})`);
      this.isVisibleSolicitMassiveSelfEvaluationModal = false;
      return;
    } else {
      try {
        this.isLoadingSolicitMassiveSelfEvaluation = true;
        this.isVisibleSolicitMassiveSelfEvaluationModal = false;
        await this.monitoringBackofficeApiService.solicitAllUsersSelfEvaluation(this.processId);
        this.onSolicitMassiveSelfEvaluation.emit();
      } catch (error) {
        this.logger.error(`Error soliciting massive self evaluation: ${error?.toString()}`);
        this.alertService.showError(this.translate.instant("admin.monitoring.details.SOLICIT_MASSIVE_SELF_EVALUATION_ERROR"));
      } finally {
        this.isVisibleSolicitMassiveSelfEvaluationModal = false;
        this.isLoadingSolicitMassiveSelfEvaluation = false;
      }
    }
  }

  protected onCancelSolicitMassiveSelfEvaluation() {
    // previene di fare annulla e chiudere la modale se stiamo caricando
    if (this.isLoadingSolicitMassiveSelfEvaluation) {
      return;
    }
    this.isVisibleSolicitMassiveSelfEvaluationModal = false;
  }

  protected showSolicitMassiveSelfEvaluationModal() {
    this.isVisibleSolicitMassiveSelfEvaluationModal = true;
  }

  protected showSolicitMassiveEvaluationModal() {
    this.isVisibleSolicitMassiveEvaluationModal = true;
  }

  protected get isSolicitMassiveSelfEvaluationDisabled(): boolean {
    if (!this.usersData || !this.processStatus || !this.processPhases) {
      return true;
    }
    // se il processo non è attivo, il pulsante deve essere disabilitato
    // se la fase di self evaluation non è attiva, il pulsante deve essere disabilitato
    // se non ho utennti in tabella, torno false
    if (this.usersData.length === 0) {
      return true;
    }
    if (!this.processPhases?.selfEvaluationPhase?.status) {
      return true;
    }
    // disattivo se non c'è nessuno che può essere sollicitato
    if (this.allUsersData.every(user => !user.canSolicitSelfEvaluation)) {
      return true;
    }
    if (this.isLoadingSolicitMassiveSelfEvaluation) {
      return true;
    }
    return this.processStatus !== ProcessStatusEnumTypes.ACTIVE || this.processPhases?.selfEvaluationPhase?.status !== PhaseStatusEnumTypes.ACTIVE;
  }

  protected get isSolicitMassiveEvaluationDisabled(): boolean {
    if (!this.usersData || !this.processStatus || !this.processPhases) {
      return true;
    }
    // se il processo non è attivo, il pulsante deve essere disabilitato
    // se la fase di evaluation non è attiva, il pulsante deve essere disabilitato
    // se non ho utennti in tabella, torno false
    if (this.usersData.length === 0) {
      return true;
    }
    if (!this.processPhases?.evaluationPhase?.status) {
      return true;
    }
    // disattivo se non c'è nessuno che può essere sollicitato
    if (this.allUsersData.every(user => !user.canSolicitEvaluation)) {
      return true;
    }
    if (this.isLoadingSolicitMassiveEvaluation) {
      return true;
    }
    return this.processStatus !== ProcessStatusEnumTypes.ACTIVE || this.processPhases?.evaluationPhase?.status !== PhaseStatusEnumTypes.ACTIVE;
  }

  protected sendCalibrationCards(user: MonitoringUserSummaryExtended): void {
    if (!user) return;
    this.onSendCalibrationCards.emit(user);
  }
}
